import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AppProvider, useApp } from './context/AppContext';
import './index.css';

import TripEntry from './pages/TripEntry';
import TripReturn from './pages/TripReturn';
import Dashboard from './pages/Dashboard';
import Alerts from './pages/Alerts';
import Manage from './pages/Manage';
import Reports from './pages/Reports';
import Login from './pages/Login';
import Assignments from './pages/Assignments';
import VehicleWash from './pages/VehicleWash';
import WashAssignments from './pages/WashAssignments';
import FuelFill from './pages/FuelFill';
import FuelStatus from './pages/FuelStatus';
import OilChangePage from './pages/OilChange';
import ActiveTrips from './pages/ActiveTrips';
import DayEnd from './pages/DayEnd';
import DriveLogs from './pages/DriveLogs';
import PoTrips from './pages/PoTrips';
import { toLocalISODate } from './utils/dateUtils';
import { getWashDue, getFuelLowDashboard, getOilChangeDue, getTrips, getDayEndChecklist } from './services/api';

const AdminChip = () => {
  const {
    admin,
    elevatedAdmin,
    isAuthenticated,
    isAdmin,
    isDispatcher,
    isStaff,
    canManageAssignments,
    logout,
    clearElevated,
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  if (isAuthenticated) {
    const roleLabel = isAdmin ? 'Admin' : (isDispatcher ? 'Dispatcher' : (isStaff ? 'Staff' : 'User'));
    return (
      <div className="sidebar-panel">
        <div className="sidebar-panel-label">Signed in ({roleLabel})</div>
        <div className="sidebar-panel-email">{admin?.email || roleLabel}</div>
        {elevatedAdmin && elevatedAdmin.email !== admin?.email && (
          <div className="sidebar-panel-sub">
            Assignments unlocked: {elevatedAdmin.email}
          </div>
        )}
        <div className="sidebar-btn-stack">
        {canManageAssignments && elevatedAdmin && (
          <button className="btn btn-sm" onClick={clearElevated}>
            End admin unlock
          </button>
        )}
        {!isAdmin && !isDispatcher && (
          <button
            className="btn btn-sm"
            onClick={() => navigate('/admin/login', { state: { from: location.pathname, elevate: true } })}
          >
            Admin login (24h)
          </button>
        )}
        <button className="btn btn-sm" onClick={() => { logout(); navigate('/login'); }}>
          Log out staff
        </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar-panel">
      <div className="sidebar-panel-label">Driver mode</div>
      <div className="sidebar-btn-stack">
      <button className="btn btn-primary btn-sm" onClick={() => navigate('/login', { state: { from: location.pathname } })}>
        Staff login
      </button>
      </div>
    </div>
  );
};

const Sidebar = () => {
  const { alerts, isAdmin, isAuthenticated, authReady } = useApp();
  const flagged = alerts.length;
  const [washDueCount, setWashDueCount] = React.useState(0);
  const [fuelLowCount, setFuelLowCount] = React.useState(0);
  const [oilDueCount, setOilDueCount] = React.useState(0);
  const [activeTripCount, setActiveTripCount] = React.useState(0);
  const [dayEndOpenCount, setDayEndOpenCount] = React.useState(0);

  React.useEffect(() => {
    if (!authReady || !isAuthenticated) return;
    const today = toLocalISODate();
    getWashDue().then((d) => setWashDueCount(d?.count || 0)).catch(() => setWashDueCount(0));
    getFuelLowDashboard().then((d) => setFuelLowCount(d?.count || 0)).catch(() => setFuelLowCount(0));
    getOilChangeDue().then((d) => setOilDueCount(d?.count || 0)).catch(() => setOilDueCount(0));
    getTrips({ status: 'pending' }).then((rows) => setActiveTripCount(Array.isArray(rows) ? rows.length : 0)).catch(() => setActiveTripCount(0));
    getDayEndChecklist(today).then((d) => setDayEndOpenCount(d?.summary?.open || 0)).catch(() => setDayEndOpenCount(0));
  }, [authReady, isAuthenticated]);

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="logo-mark">Waleed Tech</div>
        <h1>Fleet Monitor</h1>
      </div>
      <nav className="nav">
        <NavLink to="/" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">⊞</span> Dashboard</NavLink>
        <NavLink to="/assignments" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">📋</span> Daily Assignments</NavLink>
        <NavLink to="/wash-assignments" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">🧽</span> Wash Assignments</NavLink>
        <NavLink to="/fuel-fill" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">⛽</span> Fuel Fill</NavLink>
        <NavLink to="/fuel" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">🛢</span> Fuel Status{fuelLowCount > 0 && <span className="nav-badge">{fuelLowCount}</span>}</NavLink>
        <NavLink to="/trip-out" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">↗</span> Vehicle Out</NavLink>
        <NavLink to="/trip-in" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">↙</span> Vehicle In</NavLink>
        <NavLink to="/active-trips" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">📍</span> Active Trips{activeTripCount > 0 && <span className="nav-badge">{activeTripCount}</span>}</NavLink>
        <NavLink to="/day-end" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">☑</span> Day End{dayEndOpenCount > 0 && <span className="nav-badge">{dayEndOpenCount}</span>}</NavLink>
        <NavLink to="/alerts" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">⚑</span> Alerts{flagged > 0 && <span className="nav-badge">{flagged}</span>}</NavLink>
        <NavLink to="/wash" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">🧼</span> Vehicle Wash{washDueCount > 0 && <span className="nav-badge">{washDueCount}</span>}</NavLink>
        <NavLink to="/oil-change" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">🛢</span> Oil Change{oilDueCount > 0 && <span className="nav-badge">{oilDueCount}</span>}</NavLink>
        <NavLink to="/po-trips" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">📄</span> PO / Challan Trips</NavLink>
        <NavLink to="/reports" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">📊</span> Reports</NavLink>
        {isAdmin && <NavLink to="/drive-logs" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">📋</span> Drive Logs</NavLink>}
        {isAdmin && <NavLink to="/manage" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><span className="nav-icon">⚙</span> Manage</NavLink>}
      </nav>
      <AdminChip />
    </aside>
  );
};

const Layout = ({ children }) => (
  <div className="app-shell">
    <Sidebar />
    <main className="main">{children}</main>
  </div>
);

const RequireAuth = ({ children }) => {
  const { isAuthenticated, authReady } = useApp();
  const location = useLocation();
  if (!authReady) return <p className="text-muted" style={{ padding: 24 }}>Checking session…</p>;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
};

const RequireAdmin = ({ children }) => {
  const { isAdmin, authReady } = useApp();
  const location = useLocation();
  if (!authReady) return <p className="text-muted" style={{ padding: 24 }}>Checking session…</p>;
  if (!isAdmin) return <Navigate to="/admin/login" state={{ from: location.pathname, elevate: true }} replace />;
  return children;
};

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ style: { background: '#2f4370', color: '#eef2ff', border: '1px solid #3a4b75' } }} />
        <Routes>
          <Route path="/login" element={<Login portal="app" />} />
          <Route path="/admin/login" element={<Login portal="admin" />} />
          <Route path="/*" element={
            <RequireAuth>
              <Layout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/trip-out" element={<TripEntry />} />
                  <Route path="/trip-in" element={<TripReturn />} />
                  <Route path="/active-trips" element={<ActiveTrips />} />
                  <Route path="/day-end" element={<DayEnd />} />
                  <Route path="/alerts" element={<Alerts />} />
                  <Route path="/wash" element={<VehicleWash />} />
                  <Route path="/po-trips" element={<PoTrips />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/drive-logs" element={<RequireAdmin><DriveLogs /></RequireAdmin>} />
                  <Route path="/assignments" element={<Assignments />} />
                  <Route path="/wash-assignments" element={<WashAssignments />} />
                  <Route path="/fuel-fill" element={<FuelFill />} />
                  <Route path="/fuel" element={<FuelStatus />} />
                  <Route path="/oil-change" element={<OilChangePage />} />
                  <Route path="/manage" element={<RequireAdmin><Manage /></RequireAdmin>} />
                </Routes>
              </Layout>
            </RequireAuth>
          } />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}
